import { AfterViewInit, Component, NgZone, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { ElectronChannel } from '@shared/types';
import { filter } from 'rxjs';

import { AuthService } from './auth/services/auth.service';
import { AppTranslateService } from './core/services/app-translate.service';
import { CustomIconsService } from './core/services/custom-icons.service';
import { EventBusService } from './core/services/event-bus.service';
import { GoogleAnalyticsService } from './core/services/google-analytics.service';
import { PwaService } from './core/services/pwa.service';
import { UpdateService } from './core/services/update.service';
import { WebSocketService } from './core/services/websocket.service';
import { ElectronService } from './electron/electron.service';
import { ElectronScreenPickerComponent } from './electron/electron-screen-picker.component';
import { MainProcessLogService } from './electron/main-process-log.service';
import { PhoneService } from './phone/services/phone.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {
  constructor(
    private authService: AuthService,
    private eventBusService: EventBusService,
    private dialog: MatDialog,
    protected webSocketService: WebSocketService,
    private phoneService: PhoneService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private router: Router,
    private ngZone: NgZone,
    private customIconsService: CustomIconsService,
    private pwaService: PwaService,
    private appTranslateService: AppTranslateService,
    private electronService: ElectronService,
    private updateService: UpdateService,
    // Injected to activate main process log streaming in dev mode
    private mainProcessLogService: MainProcessLogService
  ) {
    this.appTranslateService.configure();
    this.setUpElectronScreenShareListener();
  }

  async ngOnInit() {
    this.pwaService.listenForInstallPrompt();
    this.customIconsService.registerIcons();
    if ('launchQueue' in window) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.launchQueue.setConsumer((launchParams) => {
        if (launchParams.targetURL.toString().includes('tel')) {
          const params = launchParams.targetURL.toString().split('/');
          if (params.length > 0) {
            this.ngZone.run(() => {
              this.router.navigateByUrl('/calls/history/' + params.at(-1));
            });
          }
        }
      });
    }

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.googleAnalyticsService.pageView(event);
    });

    // Logout event handling is now handled early in AppInitializerService

    await this.updateService.checkUpdate();
  }

  ngAfterViewInit(): void {
    // Listen for auth completion from main process
    if (this.electronService.isRunningInElectron) {
      this.electronService.on(ElectronChannel.AuthComplete, (data) => {
        console.log('[APP] Auth complete signal received:', data);

        // Only a completed exchange justifies leaving the current view. A FAILED
        // exchange used to navigate here too, which sent an already-signed-in
        // user to the login page for no reason — and leaving the shell tore down
        // the call UI. (CUC-3994)
        if (data.success && data.navigateTo) {
          // NOW it's safe to navigate - token exchange is complete
          this.router.navigate([data.navigateTo]);
        }
      });
    }
  }

  private setUpElectronScreenShareListener() {
    this.electronService.on(ElectronChannel.RequestDisplayMediaSources, (data) => {
      // Open a dialog to allow the user to select a screen
      const dialogRef = this.dialog.open(ElectronScreenPickerComponent, {
        minWidth: '50vw',
        maxWidth: '700px',
      });

      let selectedSourceId: string | null = null;

      // Set inputs and outputs here
      dialogRef.componentInstance.sources = data.sources;
      dialogRef.componentInstance.closeClicked.subscribe(() => {
        dialogRef.close();
      });
      dialogRef.componentInstance.sourceSelected.subscribe((sourceId: string) => {
        selectedSourceId = sourceId;
        this.electronService.send(ElectronChannel.DisplayMediaSourceSelected, {
          sourceId,
        });
        dialogRef.close();
        this.electronService.send(ElectronChannel.FocusWindow, { name: 'child' });
      });
      dialogRef.afterClosed().subscribe(() => {
        if (!selectedSourceId) {
          this.electronService.send(ElectronChannel.DisplayMediaSourceSelected, {
            sourceId: undefined,
          });
          this.electronService.send(ElectronChannel.FocusWindow, { name: 'child' });
        }
      });

      // After a short delay, focus the main window (the delay just makes the transition a bit less jarring)
      setTimeout(() => {
        this.electronService.send(ElectronChannel.FocusWindow, { name: 'main' });
      }, 500);
    });
  }
}
